import { TILE, TICK_MS } from '../core/constants';
import { generateGrid, Cell } from './grid';
import { Player } from './player';
import { key } from './bombs';
import { detonate } from './explosion';
import { Enemy, overlapsCircle } from './enemies';
import { mulberry32 } from '../core/rng';
import { consumePlaceBomb, consumeDetonate, readPlayer1Intent } from '../core/input';
import { planLevel } from './pickups';
import { flattenEnemies } from './levels';
import { cloneCarried, STARTING_STATS } from './campaign';
const KICK_SPEED = 2.5;
/**
 * Контейнер ігрового стану *одного* рівня.
 *
 * Гравець-переходи (next stage) робить зовнішній CampaignManager, який створює
 * нову World з тим же набором `carriedStats`. Сам World лише грає рівень
 * і повідомляє статусом, що сталося.
 */
export class World {
    config;
    grid;
    player;
    bombs;
    flames;
    enemies;
    pickups;
    exit;
    plan;
    status;
    rand;
    carried;
    /** Тіки, що залишилось до кінця рівня. */
    ticksLeft;
    /** Чи вже спавнили swarm після тайм-ауту. */
    swarmed;
    events;
    constructor(config, carried = STARTING_STATS, events = {}) {
        this.config = config;
        this.carried = cloneCarried(carried);
        this.rand = mulberry32(config.seed ^ 0xdeadbeef);
        this.grid = generateGrid(config.seed, config.softDensity);
        this.plan = planLevel(this.grid, config.seed, config.pickupCount);
        // якщо bonus — exit з плану викидаємо (немає виходу)
        if (config.bonus) {
            for (const [k, v] of [...this.plan.entries()]) {
                if (v.kind === 'exit')
                    this.plan.delete(k);
            }
        }
        this.player = new Player(1, 1, 0);
        // переносимо stats з кампанії
        this.player.stats = { ...carried.stats };
        this.player.speed = carried.speed;
        this.player.lives = carried.lives;
        this.bombs = [];
        this.flames = [];
        this.pickups = [];
        this.exit = null;
        this.enemies = this.spawnEnemies(flattenEnemies(config.enemies));
        this.status = 'playing';
        this.ticksLeft = Math.max(1, Math.floor(config.timeLimit * 1000 / TICK_MS));
        this.swarmed = false;
        this.events = events;
    }
    /** Зібрати carried-stats для передачі у наступний рівень. */
    toCarried() {
        return {
            stats: { ...this.player.stats },
            speed: this.player.speed,
            lives: this.player.lives,
            score: this.carried.score, // оновлено всередині під час tick
        };
    }
    spawnEnemies(kinds) {
        const out = [];
        const rand = mulberry32(this.config.seed ^ 0xfeedface);
        const used = new Set(['1,1']);
        for (const kind of kinds) {
            for (let i = 0; i < 500; i++) {
                const c = 1 + Math.floor(rand() * (this.grid.cols - 2));
                const r = 1 + Math.floor(rand() * (this.grid.rows - 2));
                const k = `${c},${r}`;
                if (used.has(k))
                    continue;
                if (this.grid.get(c, r) !== Cell.Floor)
                    continue;
                if (Math.abs(c - 1) + Math.abs(r - 1) < 5)
                    continue;
                used.add(k);
                out.push(new Enemy(c, r, kind));
                break;
            }
        }
        return out;
    }
    tick() {
        if (this.status !== 'playing')
            return;
        if (this.player.alive) {
            this.player.tryMove(this.grid, this.bombs, readPlayer1Intent());
            if (this.player.stats.kick)
                this.tryKick();
            if (consumePlaceBomb()) {
                const placed = this.player.placeBomb(this.grid, this.bombs);
                if (placed)
                    this.events.onBombPlace?.();
            }
            if (consumeDetonate() && this.player.stats.remote) {
                const ours = this.bombs.find(b => b.ownerId === this.player.ownerId && b.remote && !b.exploded);
                if (ours) {
                    detonate(ours, this.grid, this.bombs, this.flames, undefined, {
                        onSoftDestroyed: (c, r) => this.spawnFromPlan(c, r),
                    });
                    this.events.onExplosion?.();
                }
            }
        }
        this.player.decayIframes();
        const playerCell = { c: Math.floor(this.player.x / TILE), r: Math.floor(this.player.y / TILE) };
        for (const e of this.enemies)
            e.tick(this.grid, this.bombs, playerCell, this.rand);
        this.tickKickedBombs();
        let didExplode = false;
        for (const b of this.bombs) {
            if (b.exploded || b.remote)
                continue;
            b.fuse -= 1;
            if (b.fuse <= 0) {
                detonate(b, this.grid, this.bombs, this.flames, undefined, {
                    onSoftDestroyed: (c, r) => this.spawnFromPlan(c, r),
                });
                didExplode = true;
            }
        }
        if (didExplode)
            this.events.onExplosion?.();
        this.bombs = this.bombs.filter(b => !b.exploded);
        for (const f of this.flames)
            f.life -= 1;
        this.flames = this.flames.filter(f => f.life > 0);
        this.tryCollectPickup();
        this.applyDamage();
        // Time tick
        if (this.ticksLeft > 0) {
            this.ticksLeft--;
            if (this.ticksLeft === 0 && !this.swarmed) {
                this.timeoutSwarm();
            }
        }
        // Stage state
        const prevStatus = this.status;
        if (this.player.lives < 0) {
            this.status = 'gameover';
        }
        else if (this.config.bonus) {
            // Бонус-stage завершується коли вийшов час
            if (this.ticksLeft === 0 && this.enemies.length === 0)
                this.status = 'won';
            else if (this.ticksLeft === 0 && this.swarmed) {
                // після 10с swarm — теж переходимо
                if (this.ticksLeft <= -600)
                    this.status = 'won';
                this.ticksLeft--;
            }
        }
        else {
            if (this.exit && this.enemies.length === 0 && this.playerOnExit())
                this.status = 'won';
        }
        if (prevStatus === 'playing' && this.status === 'won')
            this.events.onStageClear?.();
    }
    /** При тайм-ауті оригінальний Bomberman додає ворогів-Pass. */
    timeoutSwarm() {
        this.swarmed = true;
        if (this.config.bonus)
            return;
        // спавнимо 5 «привидів» на випадкових клітинках, далеко від гравця
        const rand = mulberry32(this.config.seed ^ 0xbadcab);
        for (let i = 0; i < 5; i++) {
            for (let j = 0; j < 200; j++) {
                const c = 1 + Math.floor(rand() * (this.grid.cols - 2));
                const r = 1 + Math.floor(rand() * (this.grid.rows - 2));
                if (this.grid.get(c, r) !== Cell.Floor)
                    continue;
                if (Math.abs(c - 1) + Math.abs(r - 1) < 5)
                    continue;
                this.enemies.push(new Enemy(c, r, 'pass'));
                break;
            }
        }
    }
    spawnFromPlan(c, r) {
        const k = `${c},${r}`;
        const p = this.plan.get(k);
        if (!p)
            return;
        this.plan.delete(k);
        if (p.kind === 'exit')
            this.exit = { col: c, row: r };
        else
            this.pickups.push({ col: c, row: r, kind: p.kind });
    }
    playerOnExit() {
        if (!this.exit)
            return false;
        const [c, r] = this.player.centerTile();
        return c === this.exit.col && r === this.exit.row;
    }
    tryCollectPickup() {
        if (!this.player.alive || this.pickups.length === 0)
            return;
        const tk = new Set(this.player.occupiedTiles().map(([c, r]) => `${c},${r}`));
        const remaining = [];
        let pickedAny = false;
        for (const p of this.pickups) {
            if (tk.has(`${p.col},${p.row}`)) {
                this.player.applyPickup(p);
                this.carried.score += 50;
                pickedAny = true;
            }
            else
                remaining.push(p);
        }
        this.pickups = remaining;
        if (pickedAny)
            this.events.onPickup?.();
    }
    tryKick() {
        const { dx, dy } = this.player.lastIntent;
        if (dx === 0 && dy === 0)
            return;
        if (dx !== 0 && dy !== 0)
            return;
        const [pc, pr] = this.player.centerTile();
        const tc = pc + Math.sign(dx);
        const tr = pr + Math.sign(dy);
        const bomb = this.bombs.find(b => !b.exploded && !b.motion && b.col === tc && b.row === tr);
        if (!bomb)
            return;
        const nc = tc + Math.sign(dx);
        const nr = tr + Math.sign(dy);
        if (this.grid.get(nc, nr) !== Cell.Floor)
            return;
        if (this.bombs.some(b => b !== bomb && b.col === nc && b.row === nr))
            return;
        bomb.motion = { dx: Math.sign(dx), dy: Math.sign(dy) };
    }
    tickKickedBombs() {
        for (const b of this.bombs) {
            if (b.exploded || !b.motion)
                continue;
            b.x += b.motion.dx * KICK_SPEED;
            b.y += b.motion.dy * KICK_SPEED;
            const tc = b.col + b.motion.dx;
            const tr = b.row + b.motion.dy;
            const tx = tc * TILE + TILE / 2;
            const ty = tr * TILE + TILE / 2;
            const reachX = b.motion.dx === 0 || (b.motion.dx > 0 ? b.x >= tx : b.x <= tx);
            const reachY = b.motion.dy === 0 || (b.motion.dy > 0 ? b.y >= ty : b.y <= ty);
            if (reachX && reachY) {
                b.col = tc;
                b.row = tr;
                const nc = b.col + b.motion.dx;
                const nr = b.row + b.motion.dy;
                const cellOk = this.grid.get(nc, nr) === Cell.Floor;
                const noOther = !this.bombs.some(o => o !== b && o.col === nc && o.row === nr);
                const [pc, pr] = this.player.centerTile();
                const noPlayer = !(this.player.alive && pc === nc && pr === nr);
                const noEnemy = !this.enemies.some(e => {
                    const ec = e.cell();
                    return ec.c === nc && ec.r === nr;
                });
                if (!cellOk || !noOther || !noPlayer || !noEnemy) {
                    b.x = b.col * TILE + TILE / 2;
                    b.y = b.row * TILE + TILE / 2;
                    b.motion = null;
                }
            }
        }
    }
    applyDamage() {
        if (this.flames.length > 0) {
            const fc = new Set(this.flames.map(f => key(f.col, f.row)));
            const survivors = [];
            let killedAny = false;
            for (const e of this.enemies) {
                const ec = e.cell();
                if (fc.has(key(ec.c, ec.r))) {
                    this.carried.score += scoreFor(e.kind);
                    killedAny = true;
                }
                else
                    survivors.push(e);
            }
            this.enemies = survivors;
            if (killedAny)
                this.events.onEnemyDie?.();
        }
        if (!this.player.alive || this.player.iframes > 0)
            return;
        if (this.flames.length > 0 && !this.player.stats.flamePass) {
            const tiles = this.player.occupiedTiles();
            for (const [c, r] of tiles) {
                if (this.flames.some(f => f.col === c && f.row === r)) {
                    this.killPlayer();
                    return;
                }
            }
        }
        for (const e of this.enemies) {
            if (overlapsCircle(this.player.x, this.player.y, this.player.r * 0.7, e.x, e.y, e.r * 0.7)) {
                this.killPlayer();
                return;
            }
        }
    }
    killPlayer() {
        this.player.alive = false;
        this.player.lives -= 1;
        this.events.onPlayerDie?.();
        if (this.player.lives >= 0)
            this.player.respawn();
    }
    /** Час, що залишилось у секундах (для HUD). */
    secondsLeft() {
        return Math.max(0, Math.ceil(this.ticksLeft * TICK_MS / 1000));
    }
}
function scoreFor(kind) {
    switch (kind) {
        case 'balloom': return 100;
        case 'onil': return 200;
        case 'pass': return 800;
    }
}
